import React from 'react'
import {Link} from 'react-router-dom'
import {ProfileImg} from '../../../../Utils/fileUrlHelper'
import {isInterpreterAvailableForAssignment, resolveAssignStatus} from '../../../../Utils/commonData'
import type {ScoreFactor} from './InterpreterScoreBadge'
import type {AiAssignmentSummary} from './AiAgentBanner'

const OLIVE = '#a3a242'
const ASSIGN_GREEN = '#60bb1a'
const RING_RADIUS = 24
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS
const FACTOR_BAR_MAX = 3

const formatPoints = (value: number) => {
  if (value === 0) return '0'
  return (Math.round(value * 100) / 100).toString()
}

const getInitials = (name: string) => {
  const trimmed = (name ?? '').trim()
  if (!trimmed) return 'NA'
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
  }
  return trimmed.slice(0, 2).toUpperCase()
}

const getStatusStyle = (assignStatus: number | string) => {
  switch (resolveAssignStatus(assignStatus)) {
    case 0:
      return {label: 'Invited', bg: '#f8f5ff', color: '#e3759b'}
    case 1:
      return {label: 'Accepted', bg: '#f1faff', color: OLIVE}
    case 2:
      return {label: 'Declined', bg: '#fff5f8', color: '#d7383c'}
    case 3:
      return {label: 'Confirmed', bg: '#e8fff3', color: ASSIGN_GREEN}
    case 11:
      return {label: 'Unavailable', bg: '#fff8dd', color: OLIVE}
    default:
      return {label: 'Unknown', bg: '#f5f8fa', color: '#5e6278'}
  }
}

const parseRate = (rate: any) => {
  if (rate == null || rate === '') return 'N/A'
  const match = String(rate).match(/[\d.]+/)
  return match ? `$${match[0]}` : String(rate)
}

const formatDistance = (distance: any) => {
  if (distance == null || distance === '') return 'N/A'
  const str = String(distance).trim()
  if (str.toLowerCase().includes('mi')) return str
  const num = parseFloat(str)
  return Number.isFinite(num) ? `${num} mi` : str
}

const formatTravel = (travelTime: any) => {
  if (travelTime == null || travelTime === '') return 'N/A'
  const str = String(travelTime).trim()
  if (str.toLowerCase().includes('min')) return str
  const num = parseFloat(str)
  return Number.isFinite(num) ? `${num} min` : str
}

const factorBarWidth = (factor: ScoreFactor) => {
  const points = Number(factor.points ?? 0)
  return `${Math.min(100, Math.round((Math.abs(points) / FACTOR_BAR_MAX) * 100))}%`
}

const factorLabel = (factor: ScoreFactor) =>
  (factor.name ?? '').replace(/\(\d+\)/g, '').trim()

const factorValue = (factor: ScoreFactor) =>
  factor.note ? factor.note : formatPoints(factor.points)

export type AiBadge = {label: string; detail: string; tag: string; title: string}

const AI_BADGES: Record<string, AiBadge> = {
  Recommended: {
    label: 'AI RECOMMENDED',
    detail: 'HIGHEST MARGIN',
    tag: 'AI Recommended',
    title: 'Selected by the AI Assignment Agent as the highest-margin interpreter who accepted',
  },
  AwaitingConfirmation: {
    label: 'AI RECOMMENDED',
    detail: 'AWAITING CONFIRMATION',
    tag: 'AI Recommended',
    title: 'The AI Assignment Agent has asked this interpreter to confirm they will take the job',
  },
  Assigned: {
    label: 'AI ASSIGNED',
    detail: 'HIGHEST MARGIN',
    tag: 'AI Assigned',
    title: 'Assigned automatically by the AI Assignment Agent as the highest-margin interpreter who accepted',
  },
}

const AI_RECOMMENDED_OUTCOME_BADGES: Record<string, AiBadge> = {
  ConfirmedNotAssigned: {
    label: 'AI RECOMMENDED',
    detail: 'CONFIRMED',
    tag: 'AI Recommended',
    title:
      'Confirmed they will take the job, but the AI Assignment Agent could not complete the assignment. Assign them manually.',
  },
  NoConfirmation: {
    label: 'AI RECOMMENDED',
    detail: 'NEEDS CONFIRMATION',
    tag: 'AI Recommended',
    title:
      'The AI Assignment Agent’s best remaining recommendation. Nobody confirmed they would take the job, so confirm with this interpreter before assigning.',
  },
}

export const getAiBadge = (
  aiAssignment: Pick<AiAssignmentSummary, 'state' | 'outcome' | 'recommendedInterpreterId'> | null | undefined,
  interpreterId: string | null | undefined
): AiBadge | undefined => {
  if (!aiAssignment?.state || !aiAssignment.recommendedInterpreterId) return undefined

  if (
    String(interpreterId ?? '').toLowerCase() !==
    String(aiAssignment.recommendedInterpreterId).toLowerCase()
  ) {
    return undefined
  }

  if (aiAssignment.state === 'Recommended' && aiAssignment.outcome) {
    const outcomeBadge = AI_RECOMMENDED_OUTCOME_BADGES[aiAssignment.outcome]
    if (outcomeBadge) return outcomeBadge
  }

  return AI_BADGES[aiAssignment.state]
}

type Props = {
  interpreters: any[]
  availableCount: number
  onAssign: (interpreter: any) => void
  canAssign?: boolean
  aiAssignment?: AiAssignmentSummary | null
}

export const BestMatchesSection: React.FC<Props> = ({
  interpreters,
  availableCount,
  onAssign,
  canAssign = true,
  aiAssignment = null,
}) => {
  if (!interpreters.length) return null

  return (
    <div style={{marginBottom: 24}}>
      <div
        className='d-flex align-items-center justify-content-between'
        style={{marginBottom: 12}}
      >
        <div className='d-flex align-items-center' style={{gap: 10}}>
          <h2 style={{margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#181c32'}}>
            Best Matches
          </h2>
          <span
            style={{
              background: 'rgba(163,162,66,0.14)',
              color: OLIVE,
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: 999,
              fontSize: '0.8rem',
            }}
          >
            Top {interpreters.length} of {availableCount} available
          </span>
        </div>
        <span style={{color: '#a1a5b7', fontSize: '0.9rem'}}>
          <i className='bi bi-trophy' style={{marginRight: 6}} />
          Ranked by recommendation score
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 16,
        }}
      >
        {interpreters.map((item, index) => {
          const rank = index + 1
          const isTop = rank === 1
          const aiBadge = getAiBadge(aiAssignment, item?.fK_Interpreter)
          const score = Number(item?.recommendationScore ?? 0)
          const maxScore = Number(item?.recommendationMaxScore ?? 10)
          const ringDash = `${((score / maxScore) * RING_CIRCUMFERENCE).toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`
          const status = getStatusStyle(item?.assignStatus)
          const cases = item?.accountCompletedCaseCount ?? 0
          const casesLabel =
            cases === 1 ? '1 case with your account' : `${cases} cases with your account`
          const factors: ScoreFactor[] = Array.isArray(item?.scoreBreakdown)
            ? item.scoreBreakdown
            : []

          return (
            <div
              key={item?.fK_Interpreter ?? item?.code}
              style={{
                background: '#ffffff',
                borderRadius: '0.625rem',
                border: isTop ? `2px solid ${OLIVE}` : '1px solid #f1f1f2',
                padding: '20px 20px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 13,
                position: 'relative',
                boxShadow: '0 0 20px 0 rgba(76,87,125,0.03)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: -10,
                  left: 20,
                  background: isTop ? OLIVE : '#ffffff',
                  color: isTop ? '#ffffff' : OLIVE,
                  border: `1px solid ${OLIVE}`,
                  fontWeight: 600,
                  padding: '4px 12px',
                  borderRadius: 999,
                  fontSize: '0.75rem',
                  letterSpacing: '0.04em',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  maxWidth: 'calc(100% - 40px)',
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={aiBadge?.title}
              >
                {aiBadge ? (
                  <>
                    <i className='bi bi-robot' style={{fontSize: '0.85rem'}} />
                    {aiBadge.label}
                    <span style={{opacity: 0.8, fontWeight: 500}}>&mdash; {aiBadge.detail}</span>
                  </>
                ) : isTop ? (
                  'TOP MATCH'
                ) : (
                  `RECOMMENDED #${rank}`
                )}
              </span>

              <div className='d-flex align-items-center' style={{gap: 12, marginTop: 4}}>
                {item?.profileImage ? (
                  <div
                    className='rounded-circle overflow-hidden flex-shrink-0'
                    style={{width: 48, height: 48}}
                  >
                    <ProfileImg
                      imagePath={item.profileImage}
                      alt=''
                      className='rounded-circle'
                      style={{width: 48, height: 48, objectFit: 'cover'}}
                    />
                  </div>
                ) : (
                  <div
                    className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                    style={{
                      width: 48,
                      height: 48,
                      background: '#e8fff3',
                      color: ASSIGN_GREEN,
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(item?.name ?? '')}
                  </div>
                )}

                <div style={{minWidth: 0, flex: 1}}>
                  <Link
                    to={`/profile/${item?.fK_Interpreter}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-decoration-none d-block text-truncate'
                    style={{fontWeight: 700, fontSize: '1.1rem', color: '#181c32'}}
                  >
                    {item?.name ?? 'N/A'}
                  </Link>
                  <div style={{color: '#a1a5b7', fontSize: '0.9rem'}}>{casesLabel}</div>
                </div>

                <div className='position-relative flex-shrink-0' style={{width: 56, height: 56}}>
                  <svg width='56' height='56' viewBox='0 0 56 56' aria-hidden>
                    <circle
                      cx='28'
                      cy='28'
                      r={RING_RADIUS}
                      fill='none'
                      stroke='#f1f1f2'
                      strokeWidth='5'
                    />
                    <circle
                      cx='28'
                      cy='28'
                      r={RING_RADIUS}
                      fill='none'
                      stroke={OLIVE}
                      strokeWidth='5'
                      strokeLinecap='round'
                      strokeDasharray={ringDash}
                      transform='rotate(-90 28 28)'
                    />
                  </svg>
                  <div
                    className='position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center'
                  >
                    <span style={{fontWeight: 800, fontSize: '1rem', lineHeight: 1, color: '#181c32'}}>
                      {formatPoints(score)}
                    </span>
                    <span style={{fontSize: '0.6rem', color: '#a1a5b7'}}>/ 10</span>
                  </div>
                </div>
              </div>

              {factors.length > 0 && (
                <div className='d-flex flex-column' style={{gap: 6}}>
                  {factors.map((factor, factorIdx) => (
                    <div
                      key={`${factor.name}-${factorIdx}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '118px 1fr 34px',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <span style={{fontSize: '0.85rem', color: '#7e8299'}}>
                        {factorLabel(factor)}
                      </span>
                      <div
                        style={{
                          height: 5,
                          background: '#f1f1f2',
                          borderRadius: 999,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: factorBarWidth(factor),
                            background: OLIVE,
                            borderRadius: 999,
                          }}
                        />
                      </div>
                      <span
                        style={{fontSize: '0.85rem', fontWeight: 600, textAlign: 'right', color: '#181c32'}}
                      >
                        {factorValue(factor)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className='d-flex flex-wrap' style={{gap: 8}}>
                <span
                  className='d-inline-flex align-items-center'
                  style={{
                    background: '#f5f8fa',
                    color: '#5e6278',
                    borderRadius: 6,
                    padding: '5px 10px',
                    fontSize: '0.85rem',
                    gap: 6,
                  }}
                >
                  <i className='bi bi-geo-alt' style={{color: '#a1a5b7'}} />
                  {formatDistance(item?.distance)}
                </span>
                <span
                  className='d-inline-flex align-items-center'
                  style={{
                    background: '#f5f8fa',
                    color: '#5e6278',
                    borderRadius: 6,
                    padding: '5px 10px',
                    fontSize: '0.85rem',
                    gap: 6,
                  }}
                >
                  <i className='bi bi-clock' style={{color: '#a1a5b7'}} />
                  {formatTravel(item?.travelTime)}
                </span>
                <span
                  className='d-inline-flex align-items-center'
                  style={{
                    background: '#f5f8fa',
                    color: '#5e6278',
                    borderRadius: 6,
                    padding: '5px 10px',
                    fontSize: '0.85rem',
                    gap: 6,
                  }}
                >
                  <i className='bi bi-cash' style={{color: '#a1a5b7'}} />
                  {parseRate(item?.rate)}
                </span>
                <span
                  style={{
                    background: status.bg,
                    color: status.color,
                    borderRadius: 6,
                    padding: '5px 10px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}
                >
                  {status.label}
                </span>
              </div>

              <div className='d-flex' style={{gap: 8, marginTop: 2}}>
                {canAssign && isInterpreterAvailableForAssignment(item?.assignStatus) && (
                  <button
                    type='button'
                    className='border-0'
                    style={{
                      flex: 1,
                      background: ASSIGN_GREEN,
                      color: '#ffffff',
                      borderRadius: 6,
                      padding: '9px 0',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    onClick={() => onAssign(item)}
                  >
                    Assign
                  </button>
                )}
                <Link
                  to={`/profile/${item?.fK_Interpreter}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-decoration-none d-inline-flex align-items-center justify-content-center'
                  style={{
                    background: '#f5f8fa',
                    color: '#5e6278',
                    borderRadius: 6,
                    padding: '9px 16px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                  }}
                >
                  Profile
                </Link>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default BestMatchesSection
